import { useEffect, useState } from 'react';
import { Loader2, Save, Target as TargetIcon, Check } from 'lucide-react';
import { useApi } from '../api/client';

interface TargetType {
  id: number;
  short_name: string;
  description: string;
  daily: number | null;
  weekly: number | null;
}

type Edits = Record<number, { daily: string; weekly: string }>;

function toEdits(types: TargetType[]): Edits {
  return Object.fromEntries(
    types.map((t) => [
      t.id,
      {
        daily: t.daily?.toString() ?? '',
        weekly: t.weekly?.toString() ?? '',
      },
    ]),
  );
}

export default function Targets() {
  const apiFetch = useApi();
  const [types, setTypes] = useState<TargetType[] | null>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    apiFetch('/targets')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: { types: TargetType[] }) => {
        setTypes(d.types);
        setEdits(toEdits(d.types));
      })
      .catch((e) => setError(String(e)));
  }, [apiFetch]);

  function setField(typeId: number, cadence: 'daily' | 'weekly', value: string) {
    setEdits((prev) => ({
      ...prev,
      [typeId]: { ...prev[typeId], [cadence]: value },
    }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!types) return;
    setSaving(true);
    setError(null);
    const goals: { target_type_id: number; cadence: string; goal_count: number }[] = [];
    for (const t of types) {
      for (const cadence of ['daily', 'weekly'] as const) {
        const raw = edits[t.id]?.[cadence] ?? '';
        if (raw === '') continue;
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
          setError(`${t.description} ${cadence}: must be a non-negative integer`);
          setSaving(false);
          return;
        }
        goals.push({ target_type_id: t.id, cadence, goal_count: n });
      }
    }
    try {
      const r = await apiFetch('/targets', {
        method: 'PUT',
        body: JSON.stringify({ goals }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: { types: TargetType[] } = await r.json();
      setTypes(d.types);
      setEdits(toEdits(d.types));
      setSavedAt(new Date());
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  if (error && !types) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        {error}
      </div>
    );
  }
  if (!types) {
    return (
      <div className="flex items-center gap-2 text-brand-sage">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading targets…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <TargetIcon className="h-6 w-6 text-brand-lime-600" />
        <h1 className="font-display text-3xl font-black text-brand-forest">
          Targets
        </h1>
      </div>

      <form onSubmit={handleSave} className="rounded-2xl border border-brand-sage/30 bg-white p-6 shadow-sm">
        <p className="mb-4 text-sm text-brand-sage">
          Daily and weekly goals for each activity type. Leave blank to clear.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-brand-sage/20 text-left text-xs font-bold uppercase tracking-wider text-brand-sage">
                <th className="py-2 pr-4">Activity</th>
                <th className="w-40 py-2 pr-4">Daily goal</th>
                <th className="w-40 py-2">Weekly goal</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.id} className="border-b border-brand-sage/10 last:border-0">
                  <td className="py-3 pr-4 text-brand-forest">{t.description}</td>
                  <td className="py-3 pr-4">
                    <input
                      type="number"
                      min={0}
                      value={edits[t.id]?.daily ?? ''}
                      onChange={(e) => setField(t.id, 'daily', e.target.value)}
                      className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-1.5 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                    />
                  </td>
                  <td className="py-3">
                    <input
                      type="number"
                      min={0}
                      value={edits[t.id]?.weekly ?? ''}
                      onChange={(e) => setField(t.id, 'weekly', e.target.value)}
                      className="w-full rounded-md border border-brand-sage/40 bg-white px-3 py-1.5 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 rounded-xl bg-brand-lime px-4 py-2 font-display font-black text-brand-forest shadow-sm transition hover:bg-brand-lime-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving…' : 'Save targets'}
          </button>
          {savedAt && (
            <span className="flex items-center gap-1 text-sm text-brand-sage">
              <Check className="h-4 w-4 text-brand-lime-600" />
              Saved at {savedAt.toLocaleTimeString()}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}